"use client";

import { io, type Socket } from "socket.io-client";

/**
 * The one socket.io connection to the vehicle feed, shared by everything that listens to it.
 *
 * Live Tracking used to open its own socket, which was fine while it was the only consumer. Vehicle
 * events (zone, driver, ignition, speeding) are toasted on every page, so a listener mounted for the
 * whole app now needs the same feed — and two sockets would double the telemetry traffic whenever
 * the tracking page is open. Subscribers are ref-counted: the first opens the connection, the last
 * closes it, and a different token (a new login) replaces it.
 */

interface DeviceAttr {
  id: number;
  value: unknown;
  label?: string;
  valueHuman?: string;
}

/** Upstream sends either the legacy array form or an id-keyed map. */
export type DeviceAttrBag = DeviceAttr[] | Record<string, number | string>;

export interface DevicePayload {
  lat?: number;
  lon?: number;
  speed?: number;
  heading?: number;
  ts?: string;
  alt?: number;
  sats?: number;
  attrs?: DeviceAttrBag;
  status?: number;
  is_speeding?: boolean;
  driver_id?: string | null;
  imei?: string;
  /** What changed since the previous live point — see `docs/Events.MD`. Empty on history records. */
  events?: string[];
}

export interface DeviceUpdateEnvelope {
  deviceId: string;
  groupId?: string;
  payload: DevicePayload;
  ts?: number;
}

export type DeviceUpdateListener = (envelope: DeviceUpdateEnvelope) => void;

function getSocketPath(): string {
  return process.env.NEXT_PUBLIC_WS_PATH?.trim() || "/ws";
}

let socket: Socket | null = null;
let socketToken: string | null = null;
const listeners = new Set<DeviceUpdateListener>();

/**
 * The latest known state per device, folded from every envelope received. The socket stays open on
 * every page (`VehicleEventListener` subscribes app-wide), so this is how Live Tracking catches up
 * on what moved while it was not mounted, instead of waiting for each device to report again.
 * Bounded by fleet size; cleared on disconnect so one login never replays another's devices.
 */
const latestByDevice = new Map<string, DeviceUpdateEnvelope>();

/** Both bag forms as an id-keyed record, so partial bags merge by input rather than replace. */
function attrBagToRecord(bag: DeviceAttrBag | undefined): Record<string, number | string> {
  if (!bag) return {};
  if (!Array.isArray(bag)) return bag;
  const record: Record<string, number | string> = {};
  for (const attr of bag) {
    if (typeof attr.value === "number" || typeof attr.value === "string") {
      record[String(attr.id)] = attr.value;
    }
  }
  return record;
}

function rememberLatest(envelope: DeviceUpdateEnvelope) {
  const previous = latestByDevice.get(envelope.deviceId);
  // Events are one-shot notifications, not state — replaying them would mean nothing.
  const incoming = { ...envelope.payload };
  delete incoming.events;
  const payload: DevicePayload = { ...previous?.payload, ...incoming };
  if (previous?.payload.attrs || incoming.attrs) {
    payload.attrs = {
      ...attrBagToRecord(previous?.payload.attrs),
      ...attrBagToRecord(incoming.attrs),
    };
  }
  latestByDevice.set(envelope.deviceId, {
    deviceId: envelope.deviceId,
    groupId: envelope.groupId ?? previous?.groupId,
    payload,
    ts: envelope.ts ?? previous?.ts,
  });
}

/** Every device's latest folded state since the connection opened. */
export function getLatestDeviceUpdates(): DeviceUpdateEnvelope[] {
  return [...latestByDevice.values()];
}

function handleDeviceUpdate(data: unknown) {
  if (!data || typeof data !== "object") return;

  const raw = data as Partial<DeviceUpdateEnvelope>;
  if (!raw.deviceId) return;

  const envelope: DeviceUpdateEnvelope = {
    deviceId: raw.deviceId,
    groupId: raw.groupId,
    payload: raw.payload ?? {},
    ts: raw.ts,
  };

  rememberLatest(envelope);

  // Copied: a listener may unsubscribe while we iterate.
  for (const listener of [...listeners]) listener(envelope);
}

function disconnect() {
  socket?.disconnect();
  socket = null;
  socketToken = null;
  latestByDevice.clear();
}

function connect(token: string) {
  const next = io({
    path: getSocketPath(),
    auth: { hash: token },
    transports: ["websocket"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000,
    reconnectionAttempts: Infinity,
  });

  next.on("connect_error", (err) => {
    console.error("[WebSocket] Connection failed:", err.message, err);
  });
  next.on("device:update", handleDeviceUpdate);

  socket = next;
  socketToken = token;
}

/**
 * Starts receiving `device:update` envelopes for `token`. Returns the unsubscribe; the connection
 * closes once nobody is left listening.
 */
export function subscribeDeviceUpdates(
  token: string,
  listener: DeviceUpdateListener,
): () => void {
  if (socket && socketToken !== token) disconnect();

  listeners.add(listener);
  if (!socket) connect(token);

  return () => {
    listeners.delete(listener);
    // Only close the socket this subscription joined — a newer token's connection is not ours.
    if (listeners.size === 0 && socketToken === token) disconnect();
  };
}
